/**
 * Servicio de correo (botón "eMail" de V1) -> backend `IbuBack2/.../modules/mail`.
 *   POST /mail/texto          {email, subject, text}
 *   POST /mail/adjunto        {email, subject, fileName, att(base64), text}
 *   POST /mail/adjunto-doble  {email, subject, fileName, fileName2, att, att2, text}
 * `text` es HTML. `email` puede ser un correo o un arreglo de correos.
 *
 * EJEMPLO DE CABLEADO EN UNA PANTALLA (botón "eMail" -> modal -> PDF -> envío):
 *
 *   import EmailModal from '../components/common/EmailModal.jsx';
 *   import { buildTablePdf } from '../../utils/exportUtils.js';
 *   import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../services/api/mail.js';
 *
 *   const [mailOpen, setMailOpen] = useState(false);
 *   <button onClick={() => setMailOpen(true)}>eMail</button>
 *   <EmailModal
 *     open={mailOpen}
 *     onClose={() => setMailOpen(false)}
 *     titulo="Enviar Listado Cargos"
 *     onEnviar={async (email) => {
 *       const doc = await buildTablePdf('Detalle de Cargos', columnas, filasFiltradas, { totalCols });
 *       await enviarCorreoAdjunto({
 *         subject: 'Lista de Cargos',
 *         email,
 *         fileName: 'ListaCargos.pdf',
 *         docPdf: doc, // o base64: pdfADataBase64(doc)
 *         textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Cargos.'),
 *       });
 *     }}
 *   />
 *
 * `EmailModal` muestra el Swal de éxito/error (como V1) y cierra solo al terminar bien.
 * Para PDFs con `jsPDF` propio: `await agregarLogosPdf(doc)` y luego `pdfADataBase64(doc)`.
 */
import apiClient from './apiClient.js';
import { pdfADataBase64 } from '../../utils/exportUtils.js';

/**
 * HTML EXACTO del correo de V1 (idéntico en todas las pantallas; solo cambia la línea
 * del cuerpo). `cuerpo`: string, o arreglo de strings para varias líneas (cada una va en
 * su propio <tr> con el mismo estilo que "Hola,"). No se escapa: puede llevar HTML.
 */
export function plantillaCorreoV1(cuerpo) {
  const env = import.meta.env;
  const lineas = (Array.isArray(cuerpo) ? cuerpo : [cuerpo]).filter((l) => l !== undefined && l !== null && l !== '');
  const filasCuerpo = lineas
    .map(
      (l) =>
        `<tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${l}</tr>`
    )
    .join('\n         ');
  return `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${env.VITE_TAMANO_LOGO_MAIL}" align="center" src="${env.VITE_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${env.VITE_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         ${filasCuerpo}
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${env.VITE_TELEFONO_INSTITUCION}</tr>
         <tr align="right"><br>Powered by <a href="https://sistemify.com"> <img width="20%" align="center" src="${env.VITE_LINK_SISTEMA}Logo-Sistemify-PDF.png" /> </a> </tr>
         </table>`;
}

const aBase64 = (doc, base64) => base64 || (doc ? pdfADataBase64(doc) : undefined);

/** Envía un correo con UN PDF adjunto. Usa `docPdf` (jsPDF) o `base64` ya listo. */
export function enviarCorreoAdjunto({ subject, email, fileName, docPdf, base64, textoHtml }) {
  return apiClient
    .post('/mail/adjunto', { subject, email, fileName, att: aBase64(docPdf, base64), text: textoHtml })
    .then((r) => r.data);
}

/** Envía un correo con DOS PDFs adjuntos (V1 sendMailAttDoble). */
export function enviarCorreoDoble({ subject, email, fileName, fileName2, docPdf, base64, docPdf2, base64_2, textoHtml }) {
  return apiClient
    .post('/mail/adjunto-doble', {
      subject,
      email,
      fileName,
      fileName2,
      att: aBase64(docPdf, base64),
      att2: aBase64(docPdf2, base64_2),
      text: textoHtml,
    })
    .then((r) => r.data);
}

/** Envía un correo solo HTML (sin adjunto). */
export function enviarCorreoTexto({ subject, email, textoHtml }) {
  return apiClient.post('/mail/texto', { subject, email, text: textoHtml }).then((r) => r.data);
}

export default { plantillaCorreoV1, enviarCorreoAdjunto, enviarCorreoDoble, enviarCorreoTexto };
